import React, {
  useCallback,
  useRef,
  useState,
} from 'react';

import { getGroups, getSelf } from '@vlabs/api/artemy/client';
import type {
  CreatePermissionTagRequest,
  GroupRecord,
  PermissionTag,
} from '@vlabs/api/artemy/model';
import { Control } from '@vlabs/uikit';
import { Select } from '@vlabs/uikit/components/controls/select/Select';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { MultiValue } from 'react-select';

import st from './CreatePermissionTagForm.module.sass';

interface GroupOption {
  group: GroupRecord;
  label: string;
  value: string;
}

interface CreatePermissionTagFormProps {
  onCancel: VoidFunction;
  onSubmit: (values: {
    groups: GroupRecord[];
    permissionTag: PermissionTag;
  }) => Promise<'success' | 'duplicate' | 'error'>;
  isPending: boolean;
}

const GROUPS_PAGE_SIZE = 1000;

// группы фильтруются по подразделениям пользователя, но их может быть больше одной страницы
async function getGroupsDetailsPage(
  ownerDepartmentIds: string[],
  page = 1,
  groups: GroupRecord[] = [],
): Promise<GroupRecord[]> {
  const response = await getGroups({
    owner_department_ids: ownerDepartmentIds,
    page,
    page_size: GROUPS_PAGE_SIZE,
  });
  const pageItems = response.groups ?? [];
  const nextGroups = [...groups, ...pageItems];

  if (pageItems.length < GROUPS_PAGE_SIZE) {
    return nextGroups;
  }

  return getGroupsDetailsPage(ownerDepartmentIds, page + 1, nextGroups);
}

async function getAllGroupsDetails(): Promise<GroupRecord[]> {
  const selfInfo = await getSelf();
  const departmentIds = selfInfo.department_ids ?? [];

  if (!departmentIds.length) {
    return [];
  }

  return getGroupsDetailsPage(departmentIds);
}

function toGroupOptions(groupsDetails: GroupRecord[]): GroupOption[] {
  return groupsDetails
    .map((group): GroupOption => ({
      group,
      label: group.group_name ?? group.group_id ?? '',
      value: group.group_id ?? '',
    }))
    .filter(({ value }) => value !== '')
    .sort((left, right) => left.label.localeCompare(right.label));
}

function CreatePermissionTagForm({
  onCancel,
  onSubmit,
  isPending,
}: CreatePermissionTagFormProps): React.JSX.Element {
  const { t } = useTranslation();
  const [selectedGroups, setSelectedGroups] = useState<GroupOption[]>([]);
  const groupOptionsRef = useRef<GroupOption[] | null>(null);
  const {
    clearErrors,
    formState: { errors },
    handleSubmit,
    register,
    setError,
  } = useForm<CreatePermissionTagRequest>({
    defaultValues: {
      permission_tag: '',
    },
  });

  const handleGroupsChange = (nextValue: MultiValue<GroupOption>) => {
    // TODO: здесь подключится проверка группы перед добавлением в выбранные
    setSelectedGroups([...nextValue]);
  };

  const loadOptions = useCallback(async (inputValue: string) => {
    if (!groupOptionsRef.current) {
      const groupsDetails = await getAllGroupsDetails();
      groupOptionsRef.current = toGroupOptions(groupsDetails);
    }

    const normalizedInputValue = inputValue.trim().toLocaleLowerCase();

    if (!normalizedInputValue) {
      return groupOptionsRef.current ?? [];
    }

    return (groupOptionsRef.current ?? []).filter((groupOption) => (
      groupOption.label.toLocaleLowerCase().includes(normalizedInputValue)
    ));
  }, []);

  // через zIndex поднимаем меню выше модалки
  // menuPortalTarget недостаточно, он только убирает обрезание
  const styles = {
    menuPortal: (base: object) => ({
      ...base,
      zIndex: 'var(--z-index-popover)',
    }),
  };

  const onSubmitForm = handleSubmit(async ({ permission_tag: permissionTag }) => {
    const trimmedPermissionTag = permissionTag.trim();

    clearErrors('permission_tag');

    const result = await onSubmit({
      groups: selectedGroups.map(({ group }) => group),
      permissionTag: trimmedPermissionTag,
    });

    if (result === 'duplicate') {
      setError('permission_tag', {
        message: t('features:create_permission_tag.create_permission_tag_form.validation.duplicate') as unknown as string,
        type: 'duplicate',
      });
    }
  });

  return (
    <form onSubmit={onSubmitForm}>
      <div className={st.Root}>
        <h2 className={st.Title}>
          {t('features:create_permission_tag.create_permission_tag_form.title')}
        </h2>

        <div className={st.Field}>
          <Control.Input
            {...register('permission_tag', {
              required: t('features:create_permission_tag.create_permission_tag_form.validation.required') as unknown as string,
              maxLength: {
                message: t('features:create_permission_tag.create_permission_tag_form.validation.max_length') as unknown as string,
                value: 36,
              },
              validate: (value) => {
                return value.trim()
                  ? true
                  : t('features:create_permission_tag.create_permission_tag_form.validation.required') as unknown as string;
              },
              onChange: () => clearErrors('permission_tag'),
            })}
            errorMessage={errors.permission_tag?.message}
            errors={errors}
            id="permission_tag"
            label={t('features:create_permission_tag.create_permission_tag_form.permission_tag') as unknown as string}
          />
        </div>

        <div className={st.Field}>
          <Select
            cacheOptions
            classNamePrefix="CreatePermissionTagGroups"
            closeMenuOnSelect={false}
            defaultOptions
            isAsync
            isClearable
            isMulti
            label={t('features:create_permission_tag.create_permission_tag_form.groups') as unknown as string}
            loadOptions={loadOptions}
            menuPortalTarget={document.body}
            noOptionsMessage={() => t('features:create_permission_tag.create_permission_tag_form.groups_empty')}
            onChange={handleGroupsChange as never}
            placeholder={t('features:create_permission_tag.create_permission_tag_form.groups_placeholder')}
            styles={styles}
            value={selectedGroups as never}
          />
        </div>

        <div className={st.Actions}>
          <Control.Button
            className={st.Action}
            disabled={isPending}
            isLoading={isPending}
            type="submit"
          >
            {t('features:create_permission_tag.create_permission_tag_form.on_submit')}
          </Control.Button>

          <Control.Button
            className={st.Action}
            disabled={isPending}
            kind="secondary"
            onClick={onCancel}
            type="button"
            variant="outlined"
          >
            {t('features:create_permission_tag.create_permission_tag_form.on_cancel')}
          </Control.Button>
        </div>
      </div>
    </form>
  );
}

export { CreatePermissionTagForm };
